import React from "react";
import { Skeleton } from "@mui/material";
import { ChevronDown } from "lucide-react";
import { observer } from "mobx-react-lite";
import UniversalDropdown from "@/components/UniversalDropdown";
import { AUTH_ROLES } from "@/constants/auth";
import useStore from "@/hooks/useStore";
import { getUserProfileNameParts } from "@/utils/getUserProfileNameParts";
import { capitalize } from "@/utils/stringUtils";

/**
 * Sidebar user block: avatar + name/email, then a separate role row (HAI-1096).
 * Single role: right-aligned bold label. Multiple roles: view switcher dropdown
 * via {@link authStore.setActiveRole} (fail-closed against Cognito groups).
 * @param {{ isCollapsed?: boolean, signedInOnly?: boolean }} props
 */
const UserInfoCard = observer(({ isCollapsed = false, signedInOnly = false }) => {
  const { authStore } = useStore();
  const prof = getUserProfileNameParts(authStore?.userProfile);
  const displayName = signedInOnly
    ? prof.full || String(authStore?.username ?? "").trim() || String(authStore?.getSignedInEmail?.() ?? "").trim() || "User"
    : String(authStore?.fullName ?? "").trim();
  const email = signedInOnly ? "" : String(authStore?.getEffectiveEmail?.() ?? "").trim();
  const rawRole = String(authStore?.activeRole || authStore?.role || "").trim();
  const displayRole = rawRole
    ? rawRole === AUTH_ROLES.REALTOR
      ? "Real Estate Agent"
      : capitalize(rawRole, true)
    : "";
  const roles = Array.isArray(authStore?.availableRoles) ? authStore.availableRoles : [];
  const multiRole = roles.length > 1;
  const isDisplayNamePending = Boolean(authStore?.isDisplayNamePending);
  const selectRole = (role) => {
    authStore?.setActiveRole?.(role);
  };

  const first = String(signedInOnly ? prof.first : (authStore?.firstname ?? "")).trim();
  const last = String(signedInOnly ? prof.last : (authStore?.lastname ?? "")).trim();
  let avatarInitial = "U";
  if (first && last) {
    avatarInitial = `${first.charAt(0)}${last.charAt(0)}`.toUpperCase();
  } else if (first) {
    avatarInitial = first.charAt(0).toUpperCase();
  } else if (last) {
    avatarInitial = last.charAt(0).toUpperCase();
  } else {
    avatarInitial = (displayName || "U").charAt(0).toUpperCase();
  }

  const roleSection =
    !isCollapsed &&
    (isDisplayNamePending ? (
      <div className="user-info-role-section">
        <Skeleton
          data-testid="account-role-skeleton"
          variant="text"
          animation="wave"
          width="40%"
          height={18}
          sx={{ borderRadius: "4px", marginLeft: "auto" }}
        />
      </div>
    ) : (
      <div className="user-info-role-section">
        {multiRole ? (
          <UniversalDropdown
            matchTriggerWidth
            trigger={({ onClick, open }) => (
              <button
                type="button"
                className="view-selector-button"
                onClick={onClick}
                aria-haspopup="menu"
                aria-expanded={open}
                aria-label={`Current view: ${displayRole || "unknown"}. Select view.`}
              >
                <span className="view-selector-value">{displayRole || "—"}</span>
                <ChevronDown className="view-selector-chevron" size={14} aria-hidden />
              </button>
            )}
            items={roles.map((role) => {
              const isSelected = role === rawRole;
              return {
                id: role,
                selected: isSelected,
                testId: `view-option-${role}`,
                onClick: () => selectRole(role),
                label: (
                  <span>
                    {role === AUTH_ROLES.REALTOR ? "Real Estate Agent" : capitalize(role, true)}
                  </span>
                ),
                sx: {
                  borderRadius: "6px",
                  py: 1,
                  px: 1.5,
                  minHeight: 32,
                  color: isSelected ? "#9D6AC4" : "#374151",
                  bgcolor: isSelected ? "#F3F0FF" : "transparent",
                  "&:hover": {
                    bgcolor: isSelected ? "#F3F0FF" : "#F3F4F6",
                  },
                  "&.Mui-selected": {
                    color: "#9D6AC4",
                    bgcolor: "#F3F0FF",
                    "&:hover": { bgcolor: "#F3F0FF" },
                  },
                },
              };
            })}
            menuProps={{
              anchorOrigin: { vertical: "bottom", horizontal: "left" },
              transformOrigin: { vertical: "top", horizontal: "left" },
              slotProps: {
                list: { sx: { py: 0.5 } },
                paper: {
                  className: "view-selector-menu",
                  sx: {
                    mt: 0.5,
                    borderColor: "#E5E7EB",
                    borderRadius: "8px",
                    boxShadow: "0 8px 20px rgba(17, 24, 39, 0.12)",
                  },
                },
              },
            }}
          />
        ) : (
          <div className="user-info-role-label" title={displayRole || undefined}>
            {displayRole}
          </div>
        )}
      </div>
    ));

  return (
    <div className={`user-info-card${isCollapsed ? " collapsed" : ""}`}>
      <div
        className={`account-switcher-button${isDisplayNamePending ? "" : " account-user-hydrated"}`}
        style={{ cursor: "default" }}
      >
        <div className="account-avatar" style={isDisplayNamePending ? undefined : { background: "#9D6AC4" }}>
          {isDisplayNamePending ? (
            <Skeleton
              data-testid="account-avatar-skeleton"
              variant="circular"
              animation="wave"
              width={40}
              height={40}
              sx={{ marginBottom: "1px" }}
            />
          ) : (
            <span
              className="account-initial"
              style={{
                color: "#ffffff",
                WebkitTextFillColor: "#ffffff",
                textFillColor: "#ffffff",
                display: "block",
              }}
            >
              {avatarInitial}
            </span>
          )}
        </div>
        {!isCollapsed && (
          <div className="account-info">
            <div className="account-name" title={isDisplayNamePending ? undefined : displayName}>
              {isDisplayNamePending ? (
                <Skeleton
                  data-testid="account-name-skeleton"
                  variant="text"
                  animation="wave"
                  width="72%"
                  height={20}
                  sx={{ borderRadius: "4px", marginBottom: "1px" }}
                />
              ) : (
                displayName
              )}
            </div>
            {isDisplayNamePending ? null : email ? (
              <div className="account-email" title={email}>
                {email}
              </div>
            ) : null}
          </div>
        )}
      </div>
      {roleSection}
    </div>
  );
});

export default UserInfoCard;
